import { Alert, Box, FormControlLabel, Grid, Radio, TextField } from "@mui/material";
import * as React from "react";
import { ISessionState } from "../../../models/session/sessionState";
import { ConfirmationDialogResult, DatabaseRecordType, FileType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as documentService from "../../../models/services/documentService";
import { CkEditorDialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { ModifyUserInfo } from "../../framework/User/ModifyUserInfo";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { Base } from "ui/Scripts/source/framework/base";
import { IDocumentDocumentEditItem } from "source/models/documentDocument/documentDocumentEditItem";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { DocumentRemoveItem } from "source/models/document/documentRemoveItem";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { DialogTabs } from "source/components/framework/Dialog/DialogTabs";
import { DatePicker } from "source/components/framework/DateTime/DatePicker";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { FormFieldLockedToUser } from "source/components/framework/FormFieldLockedToUser";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { DocumentVersionList } from "./DocumentVersionList";
import { DocumentSaveAsTemplateDialog } from "../DocumentSaveAsTemplateDialog";
import { AppCkEditor } from "source/components/framework/AppCkEditor";
import { ApproveButton } from "source/components/framework/Button/ApproveButton";
import { PublishButton } from "source/components/framework/Button/PublishButton";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { DocumentFolderPath } from "../DocumentFolderPath/DocumentFolderPath";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

/* eslint-disable no-unused-vars */
export const enum DocumentDocumentEditDialogMenuItem {
    General = "1",
    VersionHistory = "2"
}
/* eslint-enable no-unused-vars */

export interface IDocumentDocumentEditDialogProp {
    sessionState: ISessionState;
    canEditProperties: boolean;
    id: string;
    folderId: string;
    companyId: string;
    content: string;
    landscapeOrientation: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onOk: (documentId: string, closeDialog: boolean) => void;
    onCancel: () => void;
    onRefreshList: () => Promise<void>;
}

export interface IDocumentDocumentEditDialogState {
    activeMenuItem: DocumentDocumentEditDialogMenuItem;
    id: string;
    rowId: string;
    isNew: boolean;
    path: string;
    keywords: IIdTitle[];
    code: string;
    comment: string;
    content: string;
    endDateStr: string;
    landscapeOrientation: boolean;
    keywordIds: string[];
    publishedId: string;
    item: IDocumentDocumentEditItem;
    approveUser: IModifyUser;
    nowApproved: boolean;
    publishUser: IModifyUser;
    publishVersion: number;
    nowPublished: boolean;
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
    ckEditorDataId: number;

    showSaveAsDocumentTemplateDialog: boolean;
    saveAsDocumentTemplateDialogContent: string;
}

export class DocumentDocumentEditDialog extends React.Component<IDocumentDocumentEditDialogProp, IDocumentDocumentEditDialogState> {
    private orgStateHash: string = "";
    private editor: any = null;

    constructor(props: IDocumentDocumentEditDialogProp) {
        super(props);
        this.state = {
            activeMenuItem: DocumentDocumentEditDialogMenuItem.General,
            id: "",
            rowId: "",
            isNew: false,
            path: "",
            keywords: [],
            item: null,
            code: "",
            comment: "",
            content: "",
            endDateStr: "",
            landscapeOrientation: false,
            keywordIds: [],
            publishedId: "",
            approveUser: null,
            nowApproved: false,
            lockedUser: null,
            publishUser: null,
            publishVersion: 0,
            nowPublished: false,
            isLockedToOtherUser: false,
            showSaveAsDocumentTemplateDialog: false,
            saveAsDocumentTemplateDialogContent: "",
            ckEditorDataId: 0
        };
    }

    // #region CkEditor
    private ckEditorInstanceIsReady = false;
    private ckEditorSettingData = false;

    setCkEditorContent = (value: string) => {
        this.setState({ ckEditorDataId: this.state.ckEditorDataId + 1 });
        if (!this.editor) return;
        this.ckEditorSettingData = true;
        this.editor.setData(value, {
            callback: () => {
                this.editor.resetDirty();
                this.orgStateHash = this.getHashFromState(this.props, this.state);
                this.ckEditorSettingData = false;
            }
        });
        this.setState({ ckEditorDataId: this.state.ckEditorDataId + 1 });
    };

    destroyCkEditor = () => {
        if (!this.editor) return;
        this.editor.destroy();
        this.editor = null;
    };

    handleCkEditorInstanceReady = (ev) => {
        this.editor = ev.editor;
        ev.editor.resetDirty();
        this.setCkEditorContent(this.state.content);
        this.ckEditorInstanceIsReady = true;
    };

    handleCkEditorChange = (ev) => {
        if (!this.ckEditorInstanceIsReady || this.ckEditorSettingData) return;
        this.setState({
            approveUser: null, //Reset approval
            nowApproved: false
        });
    };
    // #endregion CkEditor

    loadData = async (documentId: string) => {
        const props = this.props;
        //Unlock current message
        if (LockedRecord.isLockedToUser(this.state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, this.state.id, DatabaseRecordType.Document);
        }
        //Load and lock message
        const result = await AppUtils.callService(() => documentService.getDocumentDocumentEdit(props.companyId, props.folderId, documentId), false);
        if (!result) return;
        const item = result.item;
        const isNew = item.id === Base.emptyGuid;
        const state = {
            ...this.state,
            id: item.id,
            rowId: item.rowId,
            isNew,
            path: result.path,
            keywords: result.keywords,
            item,
            code: item.code,
            comment: item.comment,
            content: isNew ? props.content : item.content,
            landscapeOrientation: isNew ? props.landscapeOrientation : item.landscapeOrientation,
            endDateStr: item.endDate ? Base.utcTimeToDateStr(item.endDate) : "",
            keywordIds: item.keywordIds,
            publishedId: item.publishedId,
            approveUser: item.approveUser,
            nowApproved: false,
            publishUser: item.publishUser,
            publishVersion: item.publishVersion,
            nowPublished: false,
            lockedUser: item.lockedUser,
            isLockedToOtherUser: item.isLockedToOtherUser(props.sessionState.code)
        };
        this.setState(state, () => {
            this.setCkEditorContent(item.content);
        });
        //Add locked record to idb
        if (!isNew && LockedRecord.isLockedToUser(item.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(props.companyId, item.id, props.sessionState.code, DatabaseRecordType.Document));
        }
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }
    
    async componentWillUnmount(): Promise<void> {
        this.destroyCkEditor();
        //Unlock on close
        const props = this.props;
        const state = this.state;
        if (!state.isNew && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.Document);
        }
    }

    async componentDidUpdate(prevProps: IDocumentDocumentEditDialogProp, prevState: IDocumentDocumentEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && (prevProps.id === props.id || prevProps.id === Base.emptyGuid && props.id === state.id)) return;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    handleChangeEndDate = (value: moment.Moment | string) => {
        let endDateStr = "";
        if (typeof value === "string") {
            if (!Base.isNullOrEmpty(value)) {
                endDateStr = Base.timeToDateStr(value.toDate().getTime());
            }
        } else {
            endDateStr = Base.timeToDateStr(value);
        }
        this.setState({ endDateStr });
    };

    handleToggleActiveMenuItem = (activeMenuItem: DocumentDocumentEditDialogMenuItem) => {
        this.setState({ activeMenuItem });
    };

    // #region Locking
    handleUnlockAndLockToMe = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockEditableDocument, this.state.item.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const state = this.state;
        const result = await recordLockManager.lock(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.Document);
        if (!result || result.items.length < 1) return;
        this.setState({
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        });
    };
    // #endregion Locking

    getBaseSaveDataFromState = (props: IDocumentDocumentEditDialogProp, state: IDocumentDocumentEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("folderId", props.folderId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("landscapeOrientation", state.landscapeOrientation);
        result.append("endDate", state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        result.append("keywordIds", state.keywordIds);
        return result;
    };

    getHashFromState = (props: IDocumentDocumentEditDialogProp, state: IDocumentDocumentEditDialogState): string => {
        const result = this.getBaseSaveDataFromState(props, state);
        let contentDirty = false;
        if (this.editor) {
            contentDirty = this.editor.checkDirty();
        }
        result.append("changed", contentDirty);
        return result.hash;
    };

    getSaveDataFromState = (props: IDocumentDocumentEditDialogProp, state: IDocumentDocumentEditDialogState): SaveDataObj => {
        const result = this.getBaseSaveDataFromState(props, state);
        result.append("publishedId", state.publishedId);
        let content = state.content;
        if (this.editor) {
            content = this.editor.getData();
        }
        result.append("content", content);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => documentService.saveDocumentDocumentEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        if (this.editor) {
            this.editor.resetDirty();
        }
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = this.getHashFromState(props, { ...state, ...stateChange });
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
        }
        this.props.onOk(result.id, closeDialog);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveEditableDocumentParameter, state.item.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            const removeItem = new DocumentRemoveItem();
            removeItem.id = state.id;
            removeItem.code = state.item.code;
            removeItem.fileType = FileType.Document;
            const result = await AppUtils.callService(() => documentService.removeDocuments(props.companyId, props.folderId, [removeItem]), true);
            if (!result) return;
            props.onOk(null, true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onCancel();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getHashFromState(props, this.state), this.orgStateHash,
            async () => { return !!(await this.saveToDb()); },
            props.onRefreshList,
            (newStateHash: string) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    getPrintDataFromState = (props: IDocumentDocumentEditDialogProp, state: IDocumentDocumentEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("code", state.code);
        result.append("comment", state.comment);
        let content = state.content;
        if (this.editor) {
            content = this.editor.getData();
        }
        result.append("content", content);
        result.append("version", state.item.version);
        result.append("contentVersion", state.item.contentVersion);
        result.append("landscapeOrientation", state.landscapeOrientation);
        result.append("endDate", state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        result.append("createDate", state.item.createUser.date);
        result.append("createUserCode", state.item.createUser.code);
        result.append("createUserName", state.item.createUser.name);
        result.append("modifyDate", state.item.modifyUser?.date);
        result.append("modifyUserCode", state.item.modifyUser?.code);
        result.append("modifyUserName", state.item.modifyUser?.name);
        result.append("approveDate", state.approveUser?.date);
        result.append("approveUserCode", state.approveUser?.code);
        result.append("approveUserName", state.approveUser?.name);
        return result;
    };

    handlePrint = async () => {
        const props = this.props;
        const state = this.state;
        const saveData = this.getPrintDataFromState(props, state);
        saveData.append("folderId", props.folderId);
        //Call server
        const fileName = await AppUtils.callService(() => documentService.printDocument(saveData.obj));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    saveExcuteOperationAndLoad = async (operation: () => Promise<boolean>) => {
        if (!await this.saveToDb()) return;
        if (!await operation()) return;
        this.orgStateHash = this.getHashFromState(this.props, this.state);
        this.loadData(this.state.id);
    };

    confirmSaveBeforeOperation = async (confirmationTex: string, operation: () => Promise<boolean>) => {
        const props = this.props;
        const state = this.state;
        const newHash = this.getHashFromState(props, state);
        if (this.orgStateHash === newHash) {
            await operation();
            return;
        }
        if (confirmationTex) { //Save only if chosen
            const confirmationResult = await AppUtils.showConfirmationDialog(confirmationTex, false);
            if (confirmationResult === ConfirmationDialogResult.Yes) {
                await this.saveExcuteOperationAndLoad(operation);
            } else if (confirmationResult === ConfirmationDialogResult.No) {
                await operation();
            }
        } else { //Save always
            await this.saveExcuteOperationAndLoad(operation);
        }
    };

    approveDocument = async (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        const result = await AppUtils.callService(() => documentService.approveDocument(props.companyId, props.folderId, state.id), true);
        if (!result) return false;
        return new Promise(resolve => {
            this.setState({
                rowId: result.rowId,
                approveUser: StoreUtils.getNowModifyUser(),
                nowApproved: true
            }, () => {
                this.orgStateHash = this.getHashFromState(this.props, this.state);
                resolve(true);
            });
        });
    };

    handleApproveDocument = async () => {
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.EditableDocumentApproveConfirmation, state.item.code), false) === ConfirmationDialogResult.Yes) {
            await this.confirmSaveBeforeOperation(Translations.EditableDocumentSaveBeforeApproveConfirmation, this.approveDocument);
        }
    };

    publishDocument = async (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        const result = await AppUtils.callService(() => documentService.publishDocument(props.companyId, props.folderId, state.id), true);
        if (!result) return false;
        return new Promise(resolve => {
            this.setState({
                rowId: result.rowId,
                publishUser: StoreUtils.getNowModifyUser(),
                publishedId: result.publishedId,
                publishVersion: result.publishVersion,
                nowPublished: true
            }, () => {
                this.orgStateHash = this.getHashFromState(this.props, this.state);
                resolve(true);
            });
        });
    };

    handlePublishDocument = async () => {
        const state = this.state;
        if (state.isNew) return;
        if (state.approveUser) {
            if (await AppUtils.showConfirmationDialog(String.format(Translations.EditableDocumentPublishConfirmation, state.item.code), false) === ConfirmationDialogResult.Yes) {
                await this.publishDocument();
            }
        } else {
            if (await AppUtils.showConfirmationDialog(String.format(Translations.EditableDocumentPublishUnapprovedConfirmation, state.item.code), false) === ConfirmationDialogResult.Yes) {
                await this.confirmSaveBeforeOperation(null, this.publishDocument);
            }
        }
    };

    unPublishDocument = async (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        const result = await AppUtils.callService(() => documentService.unPublishDocument(props.companyId, props.folderId, state.id, state.publishedId), true);
        if (!result) return false;
        return new Promise(resolve => {
            this.setState({
                rowId: result.rowId,
                publishUser: null,
                publishedId: "",
                nowPublished: false
            }, () => {
                this.orgStateHash = this.getHashFromState(this.props, this.state);
                resolve(true);
            });
        });
    };

    handleRemovePublishedDocument = async () => {
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.EditableDocumentUnpublishConfirmation, state.item.code), false) === ConfirmationDialogResult.Yes) {
            await this.unPublishDocument();
        }
    };

    handleSaveToTemplate = () => {
        let saveAsDocumentTemplateDialogContent = this.state.content;
        if (this.editor) {
            saveAsDocumentTemplateDialogContent = this.editor.getData();
        }
        this.setState({
            showSaveAsDocumentTemplateDialog: true,
            saveAsDocumentTemplateDialogContent
        });
    };

    handleCloseSaveAsDocumentTemplateDialog = () => {
        this.setState({
            showSaveAsDocumentTemplateDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        const isPublished = !!state.publishUser;
        const isModified = !!state.item.modifyUser;
        const modifiedAfterPublish = isPublished && isModified && state.publishUser.date < state.item.modifyUser.date;
        const isNew = state.isNew;
        return (
            <CkEditorDialog
                id="documentDocumentDialog"
                maxWidth="xxl"
                maxHeight="100%"
                overflowY="hidden"
                title={isNew ? Translations.AddEditableDocument : (Translations.EditableDocument + ": " + state.item.code + (state.isLockedToOtherUser ? ", " + Translations.Locked : ""))}
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.activeMenuItem}
                        tabs={[
                            { id: DocumentDocumentEditDialogMenuItem.General, title: Translations.BasicInformation },
                            { id: DocumentDocumentEditDialogMenuItem.VersionHistory, title: Translations.VersionHistory, hidden: isNew }
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <>
                        {state.activeMenuItem === DocumentDocumentEditDialogMenuItem.General &&
                            <Box height="100%">
                                <GridRow height="calc(100% + 16px)">
                                    <Grid item sm={8} height="100%">
                                        <VerticalContainer
                                            before={<>
                                                <GridRow>
                                                    <Grid item sm={6}>
                                                        <FormField title={Translations.Name} fullWidth required margin="0">
                                                            <TextField name="code" title={Translations.Name} inputProps={{ maxLength: 80 }} value={state.code} disabled={state.isLockedToOtherUser} required={true} onChange={this.handleChange} size="small" fullWidth />
                                                            <DocumentFolderPath path={state.path} />
                                                        </FormField>
                                                    </Grid>
                                                    <Grid item sm={3}>
                                                        <FormField title={Translations.DocumentEndDate} fullWidth margin="0">
                                                            <DatePicker
                                                                value={state.endDateStr}
                                                                disabled={state.isLockedToOtherUser}
                                                                onChange={this.handleChangeEndDate}
                                                            />
                                                        </FormField>
                                                    </Grid>
                                                    <Grid item sm={3}>
                                                        <FormField title={Translations.Orientation} fullWidth margin="0">
                                                            <div style={{ display: "flex" }}>
                                                                <FormControlLabel
                                                                    label={Translations.Portrait}
                                                                    control={
                                                                        <Radio
                                                                            checked={!state.landscapeOrientation}
                                                                            disabled={state.isLockedToOtherUser}
                                                                            onChange={(_e, checked: boolean) => { this.setState({ landscapeOrientation: !checked }); }}
                                                                        />
                                                                    }
                                                                />
                                                                <FormControlLabel
                                                                    label={Translations.Landscape}
                                                                    control={
                                                                        <Radio
                                                                            checked={state.landscapeOrientation}
                                                                            disabled={state.isLockedToOtherUser}
                                                                            onChange={(_e, checked: boolean) => { this.setState({ landscapeOrientation: checked }); }}
                                                                        />
                                                                    }
                                                                />
                                                            </div>
                                                        </FormField>
                                                    </Grid>
                                                </GridRow>
                                                <GridRow>
                                                    <Grid item sm={12}>
                                                        <FormField title={Translations.Contents} fullWidth>
                                                        </FormField>
                                                    </Grid>
                                                </GridRow>
                                            </>}
                                            main={
                                                <AppCkEditor
                                                    fitToContainerHeight
                                                    readOnly={state.isLockedToOtherUser}
                                                    value={state.content}
                                                    dataId={state.ckEditorDataId}
                                                    height=""
                                                    documentLandscapeOrientation={state.landscapeOrientation}
                                                    onInstanceReady={this.handleCkEditorInstanceReady}
                                                    onChange={this.handleCkEditorChange}
                                                />
                                            }
                                        />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <VerticalContainer
                                            main={
                                                <Box sx={{ height: "100%", overflowY: "auto" }}>
                                                    <GridRow>
                                                        <Grid item sm={6}>
                                                            <FormField title={Translations.PublishVersion} fullWidth margin="0">
                                                                <TextField title={Translations.PublishVersion} value={state.publishVersion.toString(10)} disabled={true} size="small" fullWidth />
                                                            </FormField>
                                                        </Grid>
                                                        <Grid item sm={6}>
                                                            <FormField title={Translations.ContentVersion} fullWidth margin="0">
                                                                <TextField title={Translations.ContentVersion} value={state.item.contentVersion.toString(10)} disabled={true} size="small" fullWidth />
                                                            </FormField>
                                                        </Grid>
                                                    </GridRow>
                                                    <GridRow>
                                                        <Grid item sm={12}>
                                                            <FormField title={Translations.Description} fullWidth>
                                                                <TextField name="comment" title={Translations.Description} disabled={state.isLockedToOtherUser} inputProps={{ maxLength: 1000 }} value={state.comment} multiline rows={2} onChange={this.handleChange} size="small" fullWidth />
                                                            </FormField>
                                                        </Grid>
                                                    </GridRow>
                                                    <FormField title={Translations.Keywords} fullWidth>
                                                        <div style={{ width: "100%", display: "flex", flexWrap: "wrap", maxHeight: "130px", overflowY: "auto" }}>
                                                            {state.keywords.map((keyword) =>
                                                                <FormCheckBox
                                                                    key={keyword.id}
                                                                    title={keyword.title}
                                                                    checked={state.keywordIds.indexOf(keyword.id) > -1}
                                                                    disabled={state.isLockedToOtherUser}
                                                                    onChange={(checked: boolean) => {
                                                                        const keywordIds = this.state.keywordIds.filter(i => i !== keyword.id);
                                                                        if (checked) {
                                                                            keywordIds.push(keyword.id);
                                                                        }
                                                                        this.setState({ keywordIds });
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </FormField>
                                                    {!isNew &&
                                                        <>
                                                            <GridRow>
                                                                <Grid item sm={12}>
                                                                    <FormField title={Translations.DocumentCreated} fullWidth>
                                                                        <ModifyUserInfo
                                                                            modifyUser={state.item.createUser}
                                                                        />
                                                                    </FormField>
                                                                </Grid>
                                                            </GridRow>
                                                            <GridRow>
                                                                <Grid item sm={12}>
                                                                    <FormField title={Translations.DocumentModified} fullWidth>
                                                                        <ModifyUserInfo
                                                                            modifyUser={state.item.modifyUser}
                                                                        />
                                                                    </FormField>
                                                                </Grid>
                                                            </GridRow>
                                                        </>
                                                    }
                                                </Box>
                                            }
                                            after={<>
                                                {!isNew &&
                                                    <>
                                                        <GridRow>
                                                            <Grid item sm={12}>
                                                                <FormField title={Translations.DocumentApproved} fullWidth>
                                                                    <ModifyUserInfo
                                                                        modifyUser={state.approveUser}
                                                                        suffixElement={!state.isLockedToOtherUser && !state.approveUser
                                                                            ? <ApproveButton
                                                                                sx={{ marginLeft: "auto" }}
                                                                                onClick={this.handleApproveDocument}
                                                                            />
                                                                            : null
                                                                        }
                                                                    />
                                                                </FormField>
                                                            </Grid>
                                                        </GridRow>
                                                        <GridRow>
                                                            <Grid item sm={12}>
                                                                <FormField title={Translations.DocumentPublished} fullWidth>
                                                                    <ModifyUserInfo
                                                                        modifyUser={state.publishUser}
                                                                        suffixElement={!state.isLockedToOtherUser
                                                                            ? <>
                                                                                <PublishButton
                                                                                    published={isPublished}
                                                                                    modifiedAfterPublish={modifiedAfterPublish}
                                                                                    onClick={this.handlePublishDocument}
                                                                                />
                                                                                {isPublished &&
                                                                                    <RemoveToolButton
                                                                                        tooltip={Translations.Unpublish}
                                                                                        onClick={this.handleRemovePublishedDocument}
                                                                                    />
                                                                                }
                                                                            </>
                                                                            : null
                                                                        }
                                                                    />
                                                                    {isPublished && modifiedAfterPublish &&
                                                                        <Alert severity="info" sx={{ marginTop: ".5rem" }}>
                                                                            {Translations.DocumentHasBeenModifiedAfterItWasPublished}
                                                                        </Alert>
                                                                    }
                                                                </FormField>
                                                            </Grid>
                                                        </GridRow>
                                                        {state.isLockedToOtherUser &&
                                                            <GridRow>
                                                                <Grid item sm={12}>
                                                                    <FormFieldLockedToUser
                                                                        lockedUser={state.lockedUser}
                                                                        onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? this.handleUnlockAndLockToMe : null}
                                                                    />
                                                                </Grid>
                                                            </GridRow>
                                                        }
                                                    </>
                                                }
                                            </>}
                                        />
                                    </Grid>
                                </GridRow>
                            </Box>
                        }
                        {state.activeMenuItem === DocumentDocumentEditDialogMenuItem.VersionHistory &&
                            <DocumentVersionList
                                companyId={props.companyId}
                                folderId={props.folderId}
                                documentId={state.id}
                            />
                        }
                        {state.showSaveAsDocumentTemplateDialog &&
                            <DocumentSaveAsTemplateDialog
                                sessionState={props.sessionState}
                                companyId={props.companyId}
                                content={state.saveAsDocumentTemplateDialogContent}
                                landscapeOrientation={state.landscapeOrientation}
                                onClose={this.handleCloseSaveAsDocumentTemplateDialog}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={isNew}
                            prefixNode={!isNew && props.canEditProperties
                                ? <AppButton variant="outlined" onClick={this.handleSaveToTemplate} sx={{ marginRight: "1.5rem" }}>{Translations.SaveToTemplate}</AppButton>
                                : null
                            }
                            saveDisabled={state.nowApproved || state.nowPublished}
                            onCancel={this.handleCancel}
                            onPrint={!isNew ? this.handlePrint : null}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}